import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  Select,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import {
  IconCalendar,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { endOfDay, startOfDay } from "date-fns";

import { WithAppointmentsNavbar } from "@layouts/WithAppointmentsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { PAGE_TITLE } from "@constants/index";

import { scheduleQKeys } from "@hooks/queries/schedule/queryKeys";
import { useGetSchedule } from "@hooks/queries/schedule/useGetSchedule";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { CreateAppointmentModal } from "../components/CreateAppointmentModal";

import { ScheduleTable } from "./components/ScheduleTable";
import { StyledSchedule } from "./Schedule.styled";


export const UnifiedSchedule = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  useDocumentTitle(`${t("unified-schedule")} • ${PAGE_TITLE}`);

  const [searchFilter, setSearchFilter] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [dateFilterType, setDateFilterType] = useState<string | null>("today");
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startOfDay(new Date()),
    endOfDay(new Date()),
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [doctors, setDoctors] = useState<ComboboxItem[]>([]);

  const [opened, { open, close }] = useDisclosure(false);

  const { data: doctorsData } = useGetUsers({ role: "doctor" });

  const { data, isLoading, isError, isSuccess, isRefetching, fetchStatus } =
    useGetSchedule({
      search: searchFilter,
      doctor: doctorFilter,
      status: statusFilter as any,
      category: categoryFilter || undefined,
      startDate: dateRange[0] || undefined,
      endDate: dateRange[1] || undefined,
      pagination: {
        page: currentPage,
        pageSize: 10,
      },
    });

  const { buttonRef } = useRefreshAnimation({ isRefreshing: isRefetching });

  useEffect(() => {
    if (doctorsData) {
      const newData = doctorsData.map(doctor => ({
        label: doctor.fullName,
        value: doctor.id,
      }));

      setDoctors(newData);
    }
  }, [doctorsData]);

  const statusOptions: ComboboxItem[] = [
    { label: t("pending"), value: "pending" },
    { label: t("booked"), value: "booked" },
    { label: t("delivered"), value: "delivered" },
    { label: t("follow-up"), value: "follow_up" },
    { label: t("closed"), value: "closed" },
    { label: t("cancelled"), value: "cancelled" },
    { label: t("rejected"), value: "rejected" },
  ];

  const categoryOptions: ComboboxItem[] = [
    { label: t("teleconsultation"), value: "teleconsultation" },
    { label: t("homecare"), value: "homecare" },
    { label: t("labs"), value: "lab" },
  ];

  const dateFilterOptions: ComboboxItem[] = [
    { label: t("today"), value: "today" },
    { label: t("custom-range"), value: "custom" },
  ];

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithAppointmentsNavbar>
        <StyledSchedule>
          <div className="nested-header">
            <Group gap="xs">
              <TextInput
                placeholder={t("search-for-patient")}
                w={{ base: 250, md: 300 }}
                value={searchFilter}
                onChange={e => {
                  setSearchFilter(e.currentTarget.value);
                  setCurrentPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                rightSection={
                  searchFilter && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setSearchFilter("")}>
                      <IconX size={16} />
                    </ActionIcon>
                  )
                }
              />

              <Select
                placeholder={t("category")}
                data={categoryOptions}
                value={categoryFilter}
                onChange={val => {
                  setCategoryFilter(val);
                  setCurrentPage(1);
                }}
                clearable
                w={150}
              />

              <Select
                placeholder={t("status")}
                data={statusOptions}
                value={statusFilter}
                onChange={val => {
                  setStatusFilter(val);
                  setCurrentPage(1);
                }}
                clearable
                w={150}
              />

              <Select
                placeholder={t("doctor")}
                data={doctors}
                value={doctorFilter}
                onChange={val => {
                  setDoctorFilter(val || "");
                  setCurrentPage(1);
                }}
                clearable
                searchable
                w={200}
              />

              <Select
                placeholder={t("date")}
                data={dateFilterOptions}
                value={dateFilterType}
                onChange={val => {
                  setDateFilterType(val);
                  if (val === "today") {
                    setDateRange([startOfDay(new Date()), endOfDay(new Date())]);
                  } else if (val === null) {
                    // Clear date range if cleared (though not clearable currently)
                    setDateRange([null, null]);
                  }
                  setCurrentPage(1);
                }}
                w={130}
              />

              {dateFilterType === "custom" && (
                <DatePickerInput
                  type="range"
                  placeholder={t("pick-date-range")}
                  value={dateRange}
                  onChange={val => {
                    const [start, end] = val;
                    setDateRange([
                      start ? startOfDay(start) : null,
                      end ? endOfDay(end) : null,
                    ]);
                    setCurrentPage(1);
                  }}
                  leftSection={<IconCalendar size={16} stroke={1.5} />}
                  clearable
                  w={220}
                />
              )}

              <Tooltip label={t("refresh-data")} position="right">
                <ActionIcon
                  ref={buttonRef}
                  variant="default"
                  size="lg"
                  onClick={() => {
                    queryClient.invalidateQueries(scheduleQKeys.all());
                  }}>
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            <div className="wrapper">
              <CreateAppointmentModal opened={opened} close={close} />
              <PermissionGate permission="appointments.create"><Button leftSection={<IconPlus size={18} />} onClick={open}>
                {t("create-appointment")}
              </Button></PermissionGate>
            </div>
          </div>

          <ScheduleTable
            data={data?.newData}
            isLoading={isLoading}
            isError={isError}
            isSuccess={isSuccess}
            isRefetching={isRefetching}
            pagination={data?.pagination || {}}
            onPageChange={setCurrentPage}
            doctors={doctors}
          />
        </StyledSchedule>
      </WithAppointmentsNavbar>
    </WithSidebar>
  );
};
